import { Icon } from '@honestica/ui-kit/src';
import OneToneIcons from '@honestica/ui-kit/src/components/Icon/OneToneIcons';
import TwoTonesIcons from '@honestica/ui-kit/src/components/Icon/TwoTonesIcons';
import * as allAntdIcons from '@honestica/ui-kit/src/icons';
import { useState } from 'react';
import defragData from './icon-defrag-map.json';

const ICON_SIZE = 24;

const sectionTitle: React.CSSProperties = {
  fontSize: 18,
  fontWeight: 700,
  margin: '32px 0 12px',
  paddingBottom: 6,
  borderBottom: '2px solid #1677ff',
  color: '#111',
};

const tableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: 13,
};

const th: React.CSSProperties = {
  padding: '8px 12px',
  background: '#f0f0f0',
  borderBottom: '2px solid #ddd',
  textAlign: 'left',
  fontWeight: 600,
};

const td: React.CSSProperties = {
  padding: '8px 12px',
  borderBottom: '1px solid #eee',
  verticalAlign: 'middle',
};

const tdCenter: React.CSSProperties = { ...td, textAlign: 'center' };

// Dynamic discovery — all SVG assets in the ui-kit icons folder.
// To audit which ones are actually used in the codebase (excluding this sandbox), run:
//   grep -rh "from '@honestica/ui-kit/assets/icons/" packages/client/src \
//     --include="*.tsx" --include="*.ts" --exclude="*IconSandbox*" \
//     | grep -oP "(?<=icons/)[^']+(?=\.svg)" | sort -u
const svgModules = import.meta.glob<{ default: React.FC<React.SVGProps<SVGSVGElement>> }>(
  '../../../../../ui-kit/assets/icons/*.svg',
  { query: '?react', eager: true },
);

const ALL_SVG = Object.entries(svgModules)
  .map(([path, mod]) => ({
    name: path.split('/').pop()?.replace('.svg', '') ?? path,
    Icon: mod.default,
  }))
  .sort((a, b) => a.name.localeCompare(b.name));

// Dynamic discovery — all named exports from the ui-kit icons barrel.
// To audit which ones are actually used in the codebase (excluding this sandbox), run:
//   grep -rh "from '@honestica/ui-kit/src/icons'" packages/client/src \
//     --include="*.tsx" --include="*.ts" --exclude="*IconSandbox*" \
//     | grep -oP "(?<=\{)[^}]+" | tr ',' '\n' | tr -d ' ' | sort -u
const ALL_ANTD_ICONS = Object.entries(
  allAntdIcons as Record<string, React.ComponentType<{ style?: React.CSSProperties }>>,
).map(([name, AntdIcon]) => ({ name, Icon: AntdIcon }));

// Dynamic discovery — derived from OneToneIcons/TwoTonesIcons registries at runtime.
// To audit which icon names are actually used with <Icon name="…"> in the codebase, run:
//   grep -roh '<Icon[^>]*name="[^"]*"' packages/client/src \
//     --include="*.tsx" --exclude="*IconSandbox*" \
//     | grep -oP '(?<=name=")[^"]+' | sort -u
const ALL_UIKIT_ICON: Parameters<typeof Icon>[0]['name'][] = [
  ...Object.keys(OneToneIcons),
  ...Object.keys(TwoTonesIcons),
];

type InventoryItem = {
  key: string;
  preview: React.ReactNode;
  name: string;
  source: 'SVG asset' | 'UIKIT ICON component' | 'Antd Icon';
  usage: string;
};

type DefragEntry = {
  name: string;
  isNew?: boolean;
  source: 'SVG asset' | 'UIKIT ICON component' | 'Antd Icon';
};

type DefragGroup = {
  concept: string;
  note?: string;
  entries: DefragEntry[];
};

const DEFRAG_GROUPS = defragData as DefragGroup[];

const SOURCE_COLORS = {
  'SVG asset': { bg: '#fef9c3', color: '#854d0e' },
  'UIKIT ICON component': { bg: '#dcfce7', color: '#166534' },
  'Antd Icon': { bg: '#fe96e2', color: '#77661b' },
};

const ALL_INVENTORY = [
  ...ALL_SVG.map(({ name, Icon: SvgIcon }) => ({
    key: `svg-${name}`,
    preview: (
      <div>
        <SvgIcon width={ICON_SIZE} height={ICON_SIZE} />
      </div>
    ),
    name,
    source: 'SVG asset' as const,
    usage: `import ${name} from '@honestica/ui-kit/assets/icons/${name}.svg?react'`,
  })),
  ...ALL_ANTD_ICONS.map(({ name, Icon: AntdIcon }) => ({
    key: `antd-${name}`,
    preview: (
      <div>
        <AntdIcon style={{ fontSize: '24px' }} />
      </div>
    ),
    name,
    source: 'Antd Icon' as const,
    usage: `import {${name}} from '@honestica/ui-kit/src/icons'`,
  })),
  ...ALL_UIKIT_ICON.map((name) => ({
    key: `${name}`,
    preview: <Icon name={name} size={ICON_SIZE} />,
    name,
    source: 'UIKIT ICON component' as const,
    usage: `<Icon name="${name}" />`,
  })),
].sort((a, b) => a.name.localeCompare(b.name));

export function IconSandbox() {
  const [query, setQuery] = useState('');
  const [sourceFilter, setSourceFilter] = useState<InventoryItem['source'] | 'All'>('All');
  const [iconColor, setIconColor] = useState('#1890ff');

  const filtered = ALL_INVENTORY.filter((item) => {
    const terms = query
      .split('|')
      .map((t) => t.trim())
      .filter(Boolean);
    const matchesQuery =
      terms.length === 0 || terms.some((t) => item.name.toLowerCase().includes(t.toLowerCase()));
    const matchesSource = sourceFilter === 'All' || item.source === sourceFilter;
    return matchesQuery && matchesSource;
  });

  return (
    <div style={{ height: '100vh', overflowY: 'auto' }}>
      <div
        style={{ padding: '24px 32px', fontFamily: 'sans-serif', maxWidth: 1400, margin: '0 auto' }}
      >
        <h1 style={{ fontSize: 24, fontWeight: 800, marginBottom: 4 }}>Icon Sandbox</h1>
        <p>This is the Lifen Icon sandbox, use it to find the icons to add to your interfaces</p>
        <ul>
          <li>
            Prefer using 'SVG asset' icons whenever possible (this is the target implementation)
          </li>
          <li>
            If you need to add a new icon, go fetch the svg from{' '}
            <a href="https://www.figma.com/design/82Hjf1Ra17YmbJDcyk5lWd/Shape-Icons-%7C-7-816-Icons--Community-?node-id=100-4&p=f&t=oGEQHo7tCY7APWcr-0">
              Shape Icons
            </a>
          </li>
          <li>
            Functional icons must NOT contain hard coded colors (fill or stroke). If they do, they
            are likely Illustrations or Logos, which should be handled differently and not included
            here (handled how tbd)
          </li>
        </ul>

        <h2 style={sectionTitle}>Audit icons actually in use</h2>
        <p style={{ fontSize: 13, color: '#555', marginBottom: 8 }}>
          These grep commands let you see which icons are actually imported in the codebase
          (excluding this sandbox), to keep this list up to date or identify obsolete icons.
        </p>

        <p style={{ fontSize: 13, fontWeight: 600, marginTop: 16, marginBottom: 4 }}>
          SVG assets — icons imported via <code>@honestica/ui-kit/assets/icons/*.svg?react</code>:
        </p>
        <pre
          style={{
            background: '#f6f8fa',
            border: '1px solid #e1e4e8',
            borderRadius: 6,
            padding: '12px 16px',
            fontSize: 12,
            overflowX: 'auto',
            margin: 0,
          }}
        >
          {`grep -rh "from '@honestica/ui-kit/assets/icons/" packages/client/src \\
  --include="*.tsx" --include="*.ts" --exclude="*IconSandbox*" \\
  | grep -oP "(?<=icons/)[^']+(?=\\.svg)" | sort -u`}
        </pre>

        <p style={{ fontSize: 13, fontWeight: 600, marginTop: 16, marginBottom: 4 }}>
          Antd Icons — icons imported via <code>@honestica/ui-kit/src/icons</code>:
        </p>
        <pre
          style={{
            background: '#f6f8fa',
            border: '1px solid #e1e4e8',
            borderRadius: 6,
            padding: '12px 16px',
            fontSize: 12,
            overflowX: 'auto',
            margin: 0,
          }}
        >
          {`grep -rh "from '@honestica/ui-kit/src/icons'" packages/client/src \\
  --include="*.tsx" --include="*.ts" --exclude="*IconSandbox*" \\
  | grep -oP "(?<=\\\\{)[^}]+" | tr ',' '\\n' | tr -d ' ' | sort -u`}
        </pre>

        <p style={{ fontSize: 13, fontWeight: 600, marginTop: 16, marginBottom: 4 }}>
          UIKIT Icon component — names used via <code>{'<Icon name="…" />'}</code>:
        </p>
        <pre
          style={{
            background: '#f6f8fa',
            border: '1px solid #e1e4e8',
            borderRadius: 6,
            padding: '12px 16px',
            fontSize: 12,
            overflowX: 'auto',
            margin: 0,
          }}
        >
          {`grep -roh '<Icon[^>]*name="[^"]*"' packages/client/src \\
  --include="*.tsx" --exclude="*IconSandbox*" \\
  | grep -oP '(?<=name=")[^"]+' | sort -u`}
        </pre>

        <h2 style={sectionTitle}>Complete inventory ({ALL_INVENTORY.length} icons)</h2>
        <div
          style={{
            display: 'flex',
            gap: 12,
            marginBottom: 16,
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          <input
            placeholder="Filter by name…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            style={{
              padding: '6px 12px',
              border: '1px solid #d9d9d9',
              borderRadius: 6,
              fontSize: 13,
              width: 240,
              outline: 'none',
            }}
          />
          {(['All', 'SVG asset', 'UIKIT ICON component', 'Antd Icon'] as const).map((s) => (
            <button
              type="button"
              key={s}
              onClick={() => setSourceFilter(s)}
              style={{
                padding: '5px 12px',
                borderRadius: 6,
                border: '1px solid',
                cursor: 'pointer',
                fontSize: 12,
                fontWeight: sourceFilter === s ? 700 : 400,
                background: sourceFilter === s ? '#1677ff' : '#fff',
                borderColor: sourceFilter === s ? '#1677ff' : '#d9d9d9',
                color: sourceFilter === s ? '#fff' : '#333',
              }}
            >
              {s}
            </button>
          ))}
          <span style={{ color: '#999', fontSize: 12 }}>{filtered.length} results</span>
          <label
            style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#555' }}
          >
            color :
            <input
              type="text"
              placeholder="ex: red, #1677ff, currentColor…"
              value={iconColor}
              onChange={(e) => setIconColor(e.target.value)}
              style={{
                padding: '5px 10px',
                border: '1px solid #d9d9d9',
                borderRadius: 6,
                fontSize: 12,
                width: 210,
                outline: 'none',
                fontFamily: 'monospace',
                color: iconColor || '#333',
              }}
            />
            {iconColor && (
              <button
                type="button"
                onClick={() => setIconColor('')}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#aaa',
                  fontSize: 14,
                  padding: 0,
                  lineHeight: 1,
                }}
              >
                ✕
              </button>
            )}
          </label>
        </div>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={{ ...th, width: 48 }}>Icon</th>
              <th style={th}>Name</th>
              <th style={{ ...th, width: 130 }}>Source</th>
              <th style={th}>Usage</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((item) => {
              const colors = SOURCE_COLORS[item.source];
              return (
                <tr key={item.key}>
                  <td style={{ ...tdCenter, width: 48, color: iconColor || undefined }}>
                    {item.preview}
                  </td>
                  <td style={td}>
                    <strong style={{ fontSize: 13 }}>{item.name}</strong>
                  </td>
                  <td style={td}>
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 600,
                        background: colors.bg,
                        color: colors.color,
                        borderRadius: 4,
                        padding: '2px 6px',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {item.source}
                    </span>
                  </td>
                  <td style={td}>
                    <code style={{ fontSize: 11, color: '#555', wordBreak: 'break-all' }}>
                      {item.usage}
                    </code>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <h2 style={sectionTitle}>Defragmentation map ({DEFRAG_GROUPS.length} concept groups)</h2>
        <p style={{ fontSize: 13, color: '#555', marginBottom: 16 }}>
          Each group represents one visual symbol. For each group:{' '}
          <strong>does a SVG asset already exist?</strong> And which icons across the three systems
          draw the same shape and should be consolidated into a single SVG asset?
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          {DEFRAG_GROUPS.map((group) => {
            const hasSvgAsset = group.entries.some((e) => e.source === 'SVG asset');
            const resolvedEntries = group.entries.map((entry) => {
              // find the first matching item in ALL_INVENTORY (key may differ for same-name/same-source duplicates)
              const item = ALL_INVENTORY.find(
                (i) => i.name === entry.name && i.source === entry.source,
              );
              return { entry, item };
            });
            return (
              <div
                key={group.concept}
                style={{
                  border: '1px solid #e5e7eb',
                  borderRadius: 8,
                  overflow: 'hidden',
                }}
              >
                {/* Group header */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '10px 16px',
                    background: hasSvgAsset ? '#f0fdf4' : '#fafafa',
                    borderBottom: '1px solid #e5e7eb',
                  }}
                >
                  <span style={{ fontWeight: 700, fontSize: 14 }}>{group.concept}</span>
                  {hasSvgAsset ? (
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 600,
                        background: '#16a34a',
                        color: '#fff',
                        borderRadius: 4,
                        padding: '2px 7px',
                      }}
                    >
                      SVG asset exists ✓
                    </span>
                  ) : (
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 600,
                        background: '#dc2626',
                        color: '#fff',
                        borderRadius: 4,
                        padding: '2px 7px',
                      }}
                    >
                      No SVG asset yet
                    </span>
                  )}
                  {group.note && (
                    <span style={{ fontSize: 11, color: '#6b7280', fontStyle: 'italic' }}>
                      ⚠ {group.note}
                    </span>
                  )}
                </div>
                {/* Icon cards */}
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 0,
                    padding: 12,
                    background: '#fff',
                  }}
                >
                  {resolvedEntries.map(({ entry, item }) => {
                    const colors = SOURCE_COLORS[entry.source];
                    return (
                      <div
                        key={`${entry.source}-${entry.name}`}
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: 6,
                          padding: '12px 16px',
                          minWidth: 100,
                          maxWidth: 140,
                          borderRadius: 6,
                          border: '1px solid #e5e7eb',
                          margin: 4,
                          background: entry.source === 'SVG asset' ? '#fefce8' : '#fff',
                        }}
                      >
                        <div
                          style={{
                            width: 40,
                            height: 40,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: iconColor || undefined,
                          }}
                        >
                          {item ? (
                            item.preview
                          ) : (
                            <span style={{ color: '#ccc', fontSize: 10 }}>?</span>
                          )}
                        </div>
                        <span
                          style={{
                            fontSize: 10,
                            fontWeight: 600,
                            color: '#111',
                            textAlign: 'center',
                            wordBreak: 'break-word',
                            lineHeight: 1.3,
                          }}
                        >
                          {entry.name}{' '}
                          {entry.isNew && (
                            <>
                              <br />
                              <span
                                style={{
                                  backgroundColor: '#f00',
                                  color: '#fff',
                                  borderRadius: '2px',
                                  padding: '2px 3px',
                                }}
                              >
                                NEW !
                              </span>
                            </>
                          )}
                        </span>
                        <span
                          style={{
                            fontSize: 9,
                            fontWeight: 600,
                            background: colors.bg,
                            color: colors.color,
                            borderRadius: 3,
                            padding: '1px 5px',
                            whiteSpace: 'nowrap',
                            textAlign: 'center',
                          }}
                        >
                          {entry.source === 'SVG asset'
                            ? 'SVG asset'
                            : entry.source === 'UIKIT ICON component'
                              ? 'UIKit'
                              : 'Antd'}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
